import React from 'react';
import { SectionHeader } from '../ui';
import { RegulationReference } from './RegulationReference';

// ---------------------------------------------------------------------------
// Deadline calculation helpers
// ---------------------------------------------------------------------------

/** Last calendar day of the month containing `date` */
function endOfMonth(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

/** Add N calendar months, landing on the last day of the resulting month */
function addMonthsToEndOfMonth(base: Date, months: number): Date {
    const result = new Date(base.getFullYear(), base.getMonth() + months + 1, 0);
    return result;
}

function daysFrom(from: Date, to: Date): number {
    return Math.floor((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
}

function fmtDate(d: Date): string {
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

// ---------------------------------------------------------------------------
// Deadline types
// ---------------------------------------------------------------------------

type DeadlineStatus = 'overdue' | 'urgent' | 'approaching' | 'ok' | 'not_applicable' | 'unresolved';

// WP1 — trust property that may belong in the estate is not included (engine breakdown.trust_estate_inclusion_gap).
const TRUST_UNRESOLVED_ZERO =
    'No inheritance tax is calculated from the figures currently included, but trust property that may belong in the estate has not been included, so it cannot be confirmed that no tax, and no interest, is payable.';
const TRUST_UNRESOLVED_PAYMENT =
    'The tax figure is provisional because trust property that may belong in the estate has not been included, so the amount payable is not confirmed.';
export const TRUST_UNRESOLVED_DEADLINES =
    'Trust property may be missing from this calculation, so whether inheritance tax or interest is payable, and which return must be filed, need to be confirmed by a trust or inheritance tax specialist. The dates shown are the standard statutory dates.';

interface Deadline {
    id: string;
    title: string;
    subtitle: string;
    date: Date;
    daysRemaining: number;
    status: DeadlineStatus;
    note?: string;
    conditionalNote?: string;
}

function computeDeadlines(
    dod: Date,
    today: Date,
    routeKey: string | null | undefined,
    hasTaxDue: boolean,
    hasProperty: boolean,
    taxPositionUnresolved = false,
): Deadline[] {
    const eom = endOfMonth(dod);

    const deadlines: Deadline[] = [];

    // --- 1. IHT PAYMENT DEADLINE (always relevant if IHT may be due) ---
    // HMRC: IHT must be paid by the last day of the 6th month after the person died.
    // e.g. death Jan 2024 → deadline 31 Jul 2024
    const ihtPayment = addMonthsToEndOfMonth(eom, 6);
    const ihtPaymentDays = daysFrom(today, ihtPayment);

    if (routeKey !== 'POST_2022_EXCEPTED') {
        const paymentOverdue = ihtPaymentDays < 0;
        deadlines.push({
            id: 'iht_payment',
            title: hasTaxDue || taxPositionUnresolved ? 'IHT Payment Deadline' : 'IHT Payment Window',
            subtitle: hasTaxDue && !taxPositionUnresolved
                ? 'Inheritance Tax must be paid by this date'
                : taxPositionUnresolved
                ? 'The date by which any inheritance tax that is due must normally be paid'
                : 'This is the deadline by which any inheritance tax would normally need to be paid if tax were due.',
            date: ihtPayment,
            daysRemaining: ihtPaymentDays,
            // Interest only ever runs on UNPAID TAX. With no tax due there is no
            // payment obligation and no interest, so a passed date is just an
            // informational window — never "overdue", never "interest accruing".
            // WP1: with trust property possibly missing, a £0 figure is not a "no tax" answer — the date stands, but
            // neither "no payment was required" nor "no interest arises" can be said, and no liability is implied.
            status: hasTaxDue ? calcStatus(ihtPaymentDays) : taxPositionUnresolved ? 'unresolved' : 'not_applicable',
            note: taxPositionUnresolved
                ? (hasTaxDue
                    ? (paymentOverdue
                        ? `${TRUST_UNRESOLVED_PAYMENT} Interest may be accruing on any inheritance tax that is due and unpaid; review this urgently with a specialist.`
                        : `${TRUST_UNRESOLVED_PAYMENT} Any tax that is due must normally be paid by this date, before probate is granted.`)
                    : (paymentOverdue
                        ? `${TRUST_UNRESOLVED_ZERO} This date has passed: if tax is found to be due, interest runs on it from this date, so the position should be confirmed with a specialist promptly.`
                        : `${TRUST_UNRESOLVED_ZERO} If tax is found to be due, it must normally be paid by this date.`))
                : !hasTaxDue
                ? (paymentOverdue
                    ? 'No inheritance tax appears to be due, so no payment was required and no interest arises. This date marked the formal IHT payment window.'
                    : 'No inheritance tax currently appears due. If that changes, any tax would normally need to be paid by this date.')
                : (paymentOverdue
                    ? 'Interest may now be accruing on any unpaid inheritance tax. If payment has not been made, review this urgently.'
                    : 'IHT must usually be paid before probate is granted. Some assets (e.g. property) may qualify for instalments.'),
            conditionalNote: hasProperty && hasTaxDue
                ? 'IHT on certain property-related assets may be payable by annual instalments over 10 years, with interest.'
                : undefined,
        });
    }

    // --- 2. IHT RETURN FILING DEADLINE (IHT400 route only) ---
    // HMRC: IHT400 must be delivered within 12 months of the end of the month of death.
    const ihtFiling = addMonthsToEndOfMonth(eom, 12);
    const ihtFilingDays = daysFrom(today, ihtFiling);

    if (routeKey === 'POST_2022_IHT400') {
        deadlines.push({
            id: 'iht_filing',
            title: 'IHT400 Submission Deadline',
            subtitle: 'Full IHT return must be filed with HMRC',
            date: ihtFiling,
            daysRemaining: ihtFilingDays,
            status: calcStatus(ihtFilingDays),
            note: ihtFilingDays < 0
                ? 'The IHT400 deadline has passed. Submit the return to HMRC as soon as possible to avoid penalties.'
                : 'The IHT400 must normally be submitted within 12 months of the end of the month in which the death occurred.',
        });
    }

    // --- 3. PROBATE APPLICATION — soft milestone, no hard legal deadline ---
    // Convention: apply within 12 months; courts may require explanation if longer.
    // We show this as an informational milestone, not a hard deadline.
    const probateMilestone = addMonthsToEndOfMonth(eom, 12);
    const probateDays = daysFrom(today, probateMilestone);

    deadlines.push({
        id: 'probate_apply',
        title: 'Probate Application',
        subtitle: 'Recommended within 12 months of death',
        date: probateMilestone,
        daysRemaining: probateDays,
        status: probateDays < 0 ? 'approaching' : calcStatus(probateDays, true), // softer thresholds
        note: probateDays < 0
            ? 'Applying for probate more than 12 months after death is still possible but may require a written explanation.'
            : 'There is no strict legal deadline, but applying within 12 months is standard practice.',
    });

    return deadlines;
}

function calcStatus(daysRemaining: number, soft = false): DeadlineStatus {
    if (daysRemaining < 0) return 'overdue';
    if (!soft) {
        if (daysRemaining <= 30) return 'urgent';
        if (daysRemaining <= 90) return 'approaching';
    } else {
        if (daysRemaining <= 60) return 'approaching';
    }
    return 'ok';
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface StatusTreatment {
    container: string;
    eyebrow: string;
    eyebrowLabel: string;
    bodyClass: string;
    metaClass: string;
}

function statusTreatment(status: DeadlineStatus): StatusTreatment {
    switch (status) {
        case 'overdue':
            return {
                container: 'bg-white border border-[#E8E4DB] border-l-2 border-l-[#1A1A1A] p-4 rounded',
                eyebrow: 'text-[10px] tracking-[0.18em] uppercase text-[#1A1A1A]',
                eyebrowLabel: 'Overdue',
                bodyClass: 'font-serif italic text-[13px] text-[#1A1A1A] leading-relaxed',
                metaClass: 'text-[#1A1A1A]',
            };
        case 'urgent':
            return {
                container: 'bg-[#FAF3E0] border border-[#E8D89A] p-4 rounded',
                eyebrow: 'text-[10px] tracking-[0.18em] uppercase text-[#7A5A00]',
                eyebrowLabel: 'Urgent',
                bodyClass: 'text-[13px] text-[#3F3D39] leading-relaxed',
                metaClass: 'text-[#7A5A00]',
            };
        case 'approaching':
            return {
                container: 'bg-[#FAF3E0] border border-[#E8D89A] p-4 rounded',
                eyebrow: 'text-[10px] tracking-[0.18em] uppercase text-[#7A5A00]',
                eyebrowLabel: 'Approaching',
                bodyClass: 'text-[13px] text-[#3F3D39] leading-relaxed',
                metaClass: 'text-[#7A5A00]',
            };
        case 'ok':
            return {
                container: 'bg-white border border-[#E8E4DB] p-4 rounded',
                eyebrow: 'text-[10px] tracking-[0.18em] uppercase text-[#6B6762]',
                eyebrowLabel: 'On track',
                bodyClass: 'text-[13px] text-[#3F3D39] leading-relaxed',
                metaClass: 'text-[#6B6762]',
            };
        case 'unresolved':
            return {
                container: 'bg-white border border-[#E8E4DB] border-l-2 border-l-[#7A5A00] p-4 rounded',
                eyebrow: 'text-[10px] tracking-[0.18em] uppercase text-[#7A5A00]',
                eyebrowLabel: 'To be confirmed',
                bodyClass: 'text-[13px] text-[#3F3D39] leading-relaxed',
                metaClass: 'text-[#7A5A00]',
            };
        case 'not_applicable':
        default:
            return {
                container: 'bg-white border border-[#E8E4DB] p-4 rounded',
                eyebrow: 'text-[10px] tracking-[0.18em] uppercase text-[#AFA99F]',
                eyebrowLabel: 'N/A',
                bodyClass: 'text-[13px] text-[#AFA99F] leading-relaxed',
                metaClass: 'text-[#AFA99F]',
            };
    }
}

const DeadlineCard: React.FC<{ deadline: Deadline }> = ({ deadline: d }) => {
    const tr = statusTreatment(d.status);
    const absDay = Math.abs(d.daysRemaining);

    return (
        <div className={tr.container}>
            <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                    <p className={tr.eyebrow}>{tr.eyebrowLabel}</p>
                    <p className="font-serif text-[15px] font-medium text-[#1A1A1A] leading-tight mt-1">{d.title}</p>
                    <p className="text-[11px] text-[#6B6762] mt-0.5">{d.subtitle}</p>
                </div>
                <div className="text-right shrink-0">
                    <p className="font-mono tabular-nums text-[13px] text-[#1A1A1A] leading-tight">
                        {fmtDate(d.date)}
                    </p>
                    <p className={`font-mono tabular-nums text-[11px] mt-0.5 ${tr.metaClass}`}>
                        {d.status === 'not_applicable' || d.status === 'unresolved'
                            ? (d.daysRemaining < 0 ? (d.status === 'unresolved' ? 'Date passed' : 'Window passed') : `${absDay} day${absDay !== 1 ? 's' : ''} remaining`)
                            : d.daysRemaining < 0
                                ? `${absDay} day${absDay !== 1 ? 's' : ''} overdue`
                                : d.daysRemaining === 0
                                    ? 'Due today'
                                    : `${absDay} day${absDay !== 1 ? 's' : ''} remaining`}
                    </p>
                </div>
            </div>

            {d.note && (
                <p className={`${tr.bodyClass} border-t border-dotted border-[#E8E4DB] pt-2 mt-2`}>
                    {d.note}
                </p>
            )}
            {d.conditionalNote && (
                <p className="text-[11px] text-[#3F3D39] leading-relaxed mt-1 italic font-serif">
                    {d.conditionalNote}
                </p>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface Props {
    dateOfDeath: string | undefined;
    routeKey?: string | null;
    hasTaxDue?: boolean;
    hasProperty?: boolean;
    /** WP1: engine breakdown.trust_estate_inclusion_gap — trust property may be missing, so the tax position is unresolved. */
    taxPositionUnresolved?: boolean;
}

export const DeadlineTracker: React.FC<Props> = ({
    dateOfDeath,
    routeKey,
    hasTaxDue = false,
    hasProperty = false,
    taxPositionUnresolved = false,
}) => {
    if (!dateOfDeath) return null;

    const dod = new Date(dateOfDeath);
    if (isNaN(dod.getTime())) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const deadlines = computeDeadlines(dod, today, routeKey, hasTaxDue, hasProperty, taxPositionUnresolved);

    return (
        <div className="bg-white border border-[#E8E4DB] rounded overflow-hidden">
            <div className="px-6 pt-6">
                <SectionHeader
                    numeral=""
                    title="Key deadlines"
                    subtitle={`Based on date of death: ${fmtDate(dod)}`}
                />
            </div>

            <div className="px-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                {deadlines.map(d => (
                    <DeadlineCard key={d.id} deadline={d} />
                ))}
            </div>

            <div className="px-6 pt-4 pb-6 mt-4 space-y-2">
                {taxPositionUnresolved && (
                    <p data-testid="deadline-trust-unresolved" className="text-[13px] text-[#1A1A1A] leading-relaxed">
                        {TRUST_UNRESOLVED_DEADLINES}
                    </p>
                )}
                <p className="text-[11px] text-[#AFA99F] italic leading-relaxed">
                    Dates are calculated from the date of death using HMRC rules (IHT Act 1984 s226, s216).
                    Deadlines are indicative — verify with a qualified adviser for your specific circumstances.
                    Interest on unpaid IHT accrues at the HMRC late payment rate from the payment deadline.
                </p>

                <div className="flex flex-wrap gap-x-6 gap-y-1">
                    <RegulationReference
                        title="Inheritance tax payment deadline"
                        legislationReference="IHTA 1984 s226"
                    />
                    {hasProperty && (
                        <RegulationReference
                            title="Instalment option for property/business assets"
                            legislationReference="IHTA 1984 s227"
                            hmrcManualReference="IHTM30121"
                        />
                    )}
                </div>
            </div>
        </div>
    );
};
